import {
  WebGLRenderer,
  Scene,
  Fog,
  PerspectiveCamera,
  Group,
  LineBasicMaterial,
  LineSegments,
  EdgesGeometry,
  BoxGeometry,
  BufferGeometry,
  Float32BufferAttribute,
  Vector3,
  Quaternion,
  Euler,
} from "three";

interface Block {
  seg: Group;
  target: Vector3;
  quat: Quaternion;
  startAt: number;
}

export class AssemblyRenderer {
  private renderer: WebGLRenderer | null = null;
  private scene!: Scene;
  private camera!: PerspectiveCamera;
  private group!: Group;
  private mat!: LineBasicMaterial;
  private blocks: Block[] = [];
  private formIndex = -1;
  private objSpin = false;
  private zrot = 0;
  private formDoneAt = 0;
  constructor(private canvas: HTMLCanvasElement) {}

  init(): boolean {
    try {
      this.renderer = new WebGLRenderer({ canvas: this.canvas, alpha: true, antialias: true });
      this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
      this.renderer.setClearColor(0x000000, 0);
      this.scene = new Scene();
      this.scene.fog = new Fog(0x0b0b0d, 22, 66);
      this.camera = new PerspectiveCamera(52, 1, 0.1, 300);
      this.camera.position.set(0, 0, 34);
      this.camera.lookAt(0, 0, 0);
      this.group = new Group();
      this.group.position.y = -1.5;
      this.scene.add(this.group);
      this.mat = new LineBasicMaterial({ color: 0x83837e, transparent: true, opacity: 0.2 });
      const N = 48;
      for (let i = 0; i < N; i++) {
        const seg = this.makeChip();
        seg.position.set(
          (Math.random() - 0.5) * 48,
          (Math.random() - 0.5) * 32,
          (Math.random() - 0.5) * 24,
        );
        seg.rotation.set(Math.random() * 3.14, Math.random() * 3.14, Math.random() * 3.14);
        this.group.add(seg);
        this.blocks.push({ seg, target: seg.position.clone(), quat: new Quaternion(), startAt: 0 });
      }
      this.nextFormation(0);
      this.resize();
      return true;
    } catch {
      this.renderer = null;
      return false;
    }
  }

  private makeChip(): Group {
    const g = new Group();
    const v = Math.random();
    let bw: number, bh: number, bd: number, sides: string[], nPer: number;
    if (v < 0.5) {
      bw = 3.0;
      bh = 0.7;
      bd = 2.0;
      sides = ["x"];
      nPer = 6;
    } else if (v < 0.82) {
      bw = 2.4;
      bh = 0.6;
      bd = 2.4;
      sides = ["x", "z"];
      nPer = 5;
    } else {
      bw = 1.3;
      bh = 0.5;
      bd = 0.9;
      sides = ["x"];
      nPer = 2;
    }
    g.add(new LineSegments(new EdgesGeometry(new BoxGeometry(bw, bh, bd)), this.mat));
    const pins: number[] = [];
    const pinLen = 0.55,
      drop = 0.35,
      y = -bh / 2;
    const row = (axis: string) => {
      for (let s = -1; s <= 1; s += 2)
        for (let k = 0; k < nPer; k++) {
          if (axis === "x") {
            const z = -bd / 2 + (bd / nPer) * (k + 0.5),
              x0 = (s * bw) / 2,
              x1 = s * (bw / 2 + pinLen);
            pins.push(x0, y, z, x1, y, z, x1, y, z, x1, y - drop, z);
          } else {
            const x = -bw / 2 + (bw / nPer) * (k + 0.5),
              z0 = (s * bd) / 2,
              z1 = s * (bd / 2 + pinLen);
            pins.push(x, y, z0, x, y, z1, x, y, z1, x, y - drop, z1);
          }
        }
    };
    sides.forEach(row);
    const pg = new BufferGeometry();
    pg.setAttribute("position", new Float32BufferAttribute(pins, 3));
    g.add(new LineSegments(pg, this.mat));
    return g;
  }

  private objects(): Array<() => { p: Vector3; q: Quaternion }[]> {
    const face = () => new Quaternion().setFromEuler(new Euler(Math.PI / 2, 0, 0));
    const zj = () => (Math.random() - 0.5) * 5;
    return [
      () => {
        const o: { p: Vector3; q: Quaternion }[] = [];
        const N = 48,
          R = 15.5;
        for (let i = 0; i < N; i++) {
          const a = (i / N) * Math.PI * 2;
          o.push({ p: new Vector3(Math.cos(a) * R, Math.sin(a) * R, zj()), q: face() });
        }
        return o;
      },
      () => {
        const o: { p: Vector3; q: Quaternion }[] = [];
        const W = 23,
          H = 14;
        const push = (x: number, y: number) => o.push({ p: new Vector3(x, y, zj()), q: face() });
        const NT = 14;
        for (let i = 0; i < NT; i++) {
          const x = -W + (2 * W * i) / (NT - 1);
          push(x, H);
          push(x, -H);
        }
        const NS = 10;
        for (let i = 0; i < NS; i++) {
          const y = -H + (2 * H * (i + 1)) / (NS + 1);
          push(-W, y);
          push(W, y);
        }
        return o;
      },
      () => {
        const o: { p: Vector3; q: Quaternion }[] = [];
        const W = 21,
          H = 13,
          step = 1.8;
        ([
          [-1, -1],
          [1, -1],
          [-1, 1],
          [1, 1],
        ] as const).forEach(([sx, sy]) => {
          for (let k = 0; k < 6; k++)
            o.push({ p: new Vector3(sx * W - sx * k * step, sy * H, zj()), q: face() });
          for (let k = 1; k <= 6; k++)
            o.push({ p: new Vector3(sx * W, sy * H - sy * k * step, zj()), q: face() });
        });
        return o;
      },
    ];
  }

  private nextFormation(now: number): void {
    const objs = this.objects();
    this.formIndex = (this.formIndex + 1) % objs.length;
    this.objSpin = this.formIndex === 0;
    const slots = objs[this.formIndex]();
    const stagger = 0.02;
    for (let i = 0; i < this.blocks.length; i++) {
      const b = this.blocks[i];
      const s = slots[i % slots.length];
      b.target.copy(s.p);
      b.quat.copy(s.q);
      b.startAt = now + i * stagger;
    }
    this.formDoneAt = now + this.blocks.length * stagger + 4.4;
  }

  resize(): void {
    if (!this.renderer) return;
    const r = this.canvas.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return;
    this.renderer.setSize(r.width, r.height, false);
    this.camera.aspect = r.width / r.height;
    this.camera.updateProjectionMatrix();
  }

  step(t: number): void {
    if (!this.renderer) return;
    const k = 0.055;
    for (let i = 0; i < this.blocks.length; i++) {
      const b = this.blocks[i];
      if (t >= b.startAt) {
        b.seg.position.lerp(b.target, k);
        b.seg.quaternion.slerp(b.quat, k);
      }
    }
    this.zrot = this.objSpin ? this.zrot + 0.0022 : this.zrot * 0.95;
    this.group.rotation.set(0.1, 0, this.zrot);
    if (t >= this.formDoneAt) this.nextFormation(t);
    this.renderer.render(this.scene, this.camera);
  }

  dispose(): void {
    if (this.renderer) {
      try {
        // free GPU geometries + the shared material, then release the WebGL context
        // (dispose() alone leaves the context alive until GC — matters across StrictMode
        // remounts / HMR where contexts can hit the browser's ~16 live-context cap).
        for (const b of this.blocks) b.seg.traverse((o) => (o as LineSegments).geometry?.dispose?.());
        this.mat?.dispose();
        this.renderer.dispose();
        this.renderer.forceContextLoss();
      } catch {
        /* ignore */
      }
      this.renderer = null;
    }
  }
}
